import { ISpeedDialRecord } from "@webex/component-adapter-interfaces/dist/esm/src";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { SpeedDialForm, SpeedDials } from "../components/SpeedDials";
import { ISpeedDialItem } from "../components/SpeedDials/SpeedDials.types";
import { usePresenceContext } from "../contexts/PresenceContext";
import { usePresence } from "../hooks/usePresence";
import useSpeedDials, {
  addSpeedDial,
  clearSpeedDial,
  editSpeedDial,
  removeSpeedDial,
  reorderSpeedDial,
  selectSpeedDial,
  updateSpeedDial,
} from "../hooks/useSpeedDials";
import useWebexClasses from "../hooks/useWebexClasses";
import { requestParticipantAvatarUrl } from "../services/avatarService";
import { resolveWebexUserId } from "../utils/webexUserIdMapper";
import { WebexSpeedDialSearch } from "../WebexSpeedDialSearch/WebexSpeedDialSearch";

import FocusLock from "react-focus-lock";

import "./WebexSpeedDials.styles.scss";

import { LoadingSpinner } from "@momentum-ui/react-collaboration";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
} from "../../../logger/src/LocalStorage";
import {
  IFormData,
  ISpeedDialEvents,
  SpeedDialErrorModal,
  SpeedDialModal, SpeedDialView
} from "../components/SpeedDials";
import { useLocalStorageErrorNotification } from "../hooks/useLocalStorageErrorNotification";
import { submitMetrics } from "../services/WebexMetricsService";
import {
  CALLING_SPEED_DIAL_FOCUS_GROUP,
  SD_ERROR_LIST_MSG,
  SD_ERROR_NOTIFICATION,
  SD_FAILURE_NOTICATION,
  SD_SUCCESS_NOTICATION,
  SPEED_DIAL_NOT_UPTO_DATE,
  audioCall,
  crossLaunchCallMetric,
  speedDialLabel,
  videoCall,
} from "../utils/MetricUtils";

export interface IWebexSpeedDialsProps extends ISpeedDialEvents {
  userID: string;
  contacts?: ISpeedDialRecord[];
  onSortEnd?: (items?: ISpeedDialRecord[]) => void;
  onAddPress?: () => void;
  style?: React.CSSProperties;
  addSpeedDialDisabled: (isDisabled: boolean) => void;
  listError?: boolean;
  focusSpeedDialAddButton?: () => void;
  theme?:string;
}

export interface IWebexSpeedDialsHandle {
  refreshSpeedDials?: () => void;
  showAddSpeedDial: () => void;
  showSearchSpeedDials: () => void;
}

/** Prefer Graph contactId; fall back to row id (manual entries use `l_*`). */
function speedDialRowKey(item: ISpeedDialRecord): string | undefined {
  const key = item.contactId?.trim() || item.id?.trim();
  return key || undefined;
}

/** Tokens that are phone *types*, not dialable strings (bad data when saved as currentCallAddress). */
const PHONE_TYPE_TOKENS = new Set([
  "work",
  "mobile",
  "home",
  "mail",
  "other",
  "sip",
  "extension",
  "email",
]);

function isDialableIdentityString(value?: string): boolean {
  if (!value?.trim()) return false;
  const t = value.trim().toLowerCase();
  if (PHONE_TYPE_TOKENS.has(t)) return false;
  return /\d/.test(value) || value.includes("@");
}

function pickPhoneFromGraphPhoneNumbers(
  item: ISpeedDialRecord,
  phoneType?: string
): string | undefined {
  const list = item.phoneNumbers as
    | Array<{ type?: string; address?: string; value?: string }>
    | undefined;
  if (!list?.length) return undefined;
  const pt = phoneType?.toLowerCase();
  if (pt) {
    const match = list.find((p) => p?.type?.toLowerCase() === pt);
    const addr = match?.address ?? match?.value;
    if (addr && isDialableIdentityString(addr)) return addr;
  }
  for (const p of list) {
    const addr = p?.address ?? p?.value;
    if (addr && isDialableIdentityString(addr)) return addr;
  }
  return undefined;
}

/** True when value looks like an email, not a phone-type token mistakenly stored as address. */
function isPlausibleEmailAddress(value?: string): boolean {
  if (!value?.trim()) return false;
  const t = value.trim().toLowerCase();
  if (PHONE_TYPE_TOKENS.has(t)) return false;
  return value.includes("@");
}

/**
 * Prefer Graph `mail` / `emailAddresses`; use `currentCallAddress` only when it contains @.
 * Fixes rows where contact submit stored "mail" as currentCallAddress instead of the real address.
 */
function pickEmailFromGraphContact(item: ISpeedDialRecord): string | undefined {
  const fromMail = item.mail?.trim();
  if (isPlausibleEmailAddress(fromMail)) return fromMail;

  // Same as WebexSearchContactsList: first Graph email (search uses emailAddresses[0])
  const fromFirst = (
    item.emailAddresses as { address?: string }[] | undefined
  )?.[0]?.address?.trim();
  if (isPlausibleEmailAddress(fromFirst)) return fromFirst;

  const list = item.emailAddresses as
    | Array<{ type?: string; address?: string }>
    | undefined;
  if (list?.length) {
    const pt = item.phoneType?.toLowerCase();
    if (pt === "mail") {
      const typed = list.find(
        (e) =>
          e?.type?.toLowerCase() === "mail" && isPlausibleEmailAddress(e?.address)
      );
      if (typed?.address) return typed.address.trim();
    }
    for (const e of list) {
      const a = e?.address?.trim();
      if (isPlausibleEmailAddress(a)) return a;
    }
  }

  const cca = item.currentCallAddress?.trim();
  if (isPlausibleEmailAddress(cca)) return cca;

  return undefined;
}

function getSpeedDialIdentityFields(item: ISpeedDialRecord) {
  let email = pickEmailFromGraphContact(item);
  const pn0 = item.phoneNumbers?.[0] as
    | { address?: string; value?: string }
    | undefined;
  let phone =
    item.phone ||
    item.mobilePhone ||
    item.businessPhones?.[0] ||
    pn0?.address ||
    pn0?.value;
  // Graph/adapter sometimes stores phone *type* ("work", "mobile") in `phone` — that blocks using currentCallAddress below.
  if (phone && !isDialableIdentityString(phone)) {
    phone = undefined;
  }
  if (
    !phone &&
    item.currentCallAddress &&
    !item.currentCallAddress.includes("@") &&
    !PHONE_TYPE_TOKENS.has(item.currentCallAddress.trim().toLowerCase())
  ) {
    phone = item.currentCallAddress;
  }
  if (!isDialableIdentityString(phone)) {
    phone = pickPhoneFromGraphPhoneNumbers(item, item.phoneType) ?? phone;
  }
  // Last resort: dialable digits in currentCallAddress (e.g. "+91 80 4536 4277") when graph phone list was empty
  if (!isDialableIdentityString(phone) && item.currentCallAddress?.trim()) {
    const cca = item.currentCallAddress.trim();
    if (
      !cca.includes("@") &&
      !PHONE_TYPE_TOKENS.has(cca.toLowerCase()) &&
      isDialableIdentityString(cca)
    ) {
      phone = cca;
    }
  }
  if (!isDialableIdentityString(phone)) {
    phone = undefined;
  }
  return {
    email,
    phone,
    displayName: item.displayName,
  };
}

/** Rows we can ask Webex People to resolve (email, or displayName+phone, or directory contactId). */
function shouldResolveSpeedDialWebexUser(item: ISpeedDialRecord): boolean {
  const { email, phone, displayName } = getSpeedDialIdentityFields(item);
  if (email?.trim()) return true;
  if (displayName?.trim() && phone?.trim()) return true;
  if (item.contactId?.trim()) return true;
  return false;
}

/**
 * @description The summary of this component.
 * @param {IWebexSpeedDialsProps} param An object of IWebexSpeedDialsProps
 * @param {string} param.userID The current user ID
 * @param {Function} param.onPress Handler to call speed dial item is pressed.
 * @param {Function} param.onSortEnd Handler to call when sorting ends
 * @param {Function} param.onEditPress Function to call when edit action is pressed.
 * @param {Function} param.onRemovePress Function to call when remove action is pressed.
 * @param {Function} param.onVideoCallPress Function to call when video call button pressed.
 * @param {Function} param.onAudioCallPress Function to call when audio call button pressed.
 * @returns {React.Component} WebexSpeedDials component
 */
export const WebexSpeedDials = forwardRef<
  IWebexSpeedDialsHandle,
  IWebexSpeedDialsProps
>(
  (
    {
      userID,
      addSpeedDialDisabled,
      contacts = undefined,
      onPress = undefined,
      onSortEnd = undefined,
      onRemovePress = undefined,
      onEditPress = undefined,
      onAudioCallPress = undefined,
      onVideoCallPress = undefined,
      onAddPress = undefined,
      style = undefined,
      focusSpeedDialAddButton = undefined,
      theme
    },
    ref
  ) => {
    const { t } = useTranslation("WebexSpeedDials");
    const [cssClasses] = useWebexClasses("speed-dial-widget");
    const {
      speedDials,
      selectedItem,
      dispatch,
      selectedIndex,
      loading,
      refresh,
    } = useSpeedDials(userID, contacts);

    const { getPresence, registerUsers } = usePresence("speedDial");
    const { version: presenceVersion, manager: presenceManager } = usePresenceContext();

    // Map speed-dial row key (contactId or row id) → Webex user ID
    const [rowKeyToWebexUserId, setRowKeyToWebexUserId] = useState<Map<string, string>>(new Map());
    // Resolve Webex user IDs for presence + HTTPS avatars (includes manual `l_*` rows when email/phone+name exist).
    // Merge each resolution as it completes (parallel lookups) so new rows update immediately instead of waiting
    // for every row like Promise.all did.
    useEffect(() => {
      let cancelled = false;

      const resolveAll = async () => {
        if (!speedDials || speedDials.length === 0) {
          setRowKeyToWebexUserId(new Map());
          return;
        }

        const validKeys = new Set(
          speedDials
            .map((item) => speedDialRowKey(item))
            .filter((k): k is string => Boolean(k))
        );

        setRowKeyToWebexUserId((prev) => {
          const next = new Map<string, string>();
          prev.forEach((v, k) => {
            if (validKeys.has(k)) {
              next.set(k, v);
            }
          });
          return next;
        });

        const itemsToResolve = speedDials.filter(shouldResolveSpeedDialWebexUser);

        await Promise.all(
          itemsToResolve.map(async (item) => {
            const rowKey = speedDialRowKey(item);
            if (!rowKey) {
              return;
            }

            const { email, phone, displayName } = getSpeedDialIdentityFields(item);
            const resolverInput = {
              contactId: item.contactId || item.id,
              displayName,
              email,
              phone,
            };
            const resolved = await resolveWebexUserId(resolverInput);

            if (cancelled) {
              return;
            }

            if (!resolved) {
              return;
            }

            setRowKeyToWebexUserId((prev) => {
              if (!validKeys.has(rowKey)) {
                return prev;
              }
              const next = new Map(prev);
              next.set(rowKey, resolved);
              return next;
            });
          })
        );
      };

      void resolveAll();

      return () => {
        cancelled = true;
      };
    }, [speedDials]);
    

    const webexUserIds = useMemo(
      () => Array.from(rowKeyToWebexUserId.values()).filter(Boolean),
      [rowKeyToWebexUserId]
    );

    const webexUserIdsKey = useMemo(
      () => [...webexUserIds].sort().join(","),
      [webexUserIds]
    );

    // Sync whenever PresenceManager becomes available OR the id set changes (incl. ids resolving after manager was ready)
    useEffect(() => {
      if (!presenceManager) {
        return;
      }
      if (webexUserIds.length > 0) {
        registerUsers(webexUserIds, { force: true });
      } else {
        registerUsers([]);
      }
    }, [presenceManager, webexUserIdsKey, webexUserIds, registerUsers]);

    /**
     * Same pipeline as WebexSearchContactsList: resolveWebexUserId → getPresence + requestParticipantAvatarUrl.
     * Without a resolved Webex person id, presence stays unset and Avatar uses Graph fetchAvatarUrl/initials.
     */
    const speedDialsWithPresence = useMemo(() => {
      return speedDials.map((item: ISpeedDialRecord): ISpeedDialItem => {
        const rowKey = speedDialRowKey(item);
        const webexUserId = rowKey ? rowKeyToWebexUserId.get(rowKey) : undefined;

        const presence = webexUserId ? getPresence(webexUserId) : undefined;

        const graphFetch = (item as ISpeedDialItem).fetchAvatarUrl;
        const fetchAvatarUrl = webexUserId
          ? () => requestParticipantAvatarUrl(webexUserId, { size: 40 })
          : graphFetch;

        return {
          ...item,
          presenceType: presence?.presenceType,
          presenceLabel: presence?.presenceLabel,
          fetchAvatarUrl,
        };
      });
    }, [speedDials, rowKeyToWebexUserId, getPresence, presenceVersion]);

    const [currentView, setCurrentView] = useState("list");
    const addHeaderText = t("modal.add.header");
    const editHeaderText = t("modal.edit.header");
    const [errorText, setErrorText] = useState<string>("");
    const [searchText, setSearchText] = useState();
    const [speedDialListError, setSpeedDialListError] =
      useState<boolean>(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const sdFailureAdd = useLocalStorageErrorNotification(
      SD_FAILURE_NOTICATION.SD_FAILURE_ADD
    );
    const sdFailureDelete = useLocalStorageErrorNotification(
      SD_FAILURE_NOTICATION.SD_FAILURE_DELETE
    );
    const sdFailureEdit = useLocalStorageErrorNotification(
      SD_FAILURE_NOTICATION.SD_FAILURE_EDIT
    );
    const sdFailureReorder = useLocalStorageErrorNotification(
      SD_FAILURE_NOTICATION.SD_FAILURE_REORDER
    );
    const removeSdFailureStorageEvents = () => {
      removeLocalStorageItem(SD_FAILURE_NOTICATION.SD_FAILURE_ADD);
      removeLocalStorageItem(SD_FAILURE_NOTICATION.SD_FAILURE_DELETE);
      removeLocalStorageItem(SD_FAILURE_NOTICATION.SD_FAILURE_EDIT);
      removeLocalStorageItem(SD_FAILURE_NOTICATION.SD_FAILURE_REORDER);
    };

    useImperativeHandle(ref, () => ({
      showAddSpeedDial() {
        setCurrentView(SpeedDialView.ADDVIEW);
      },
      showSearchSpeedDials() {
        setCurrentView(SpeedDialView.SEARCHVIEW);
      },
    }));

    const handleEditPress = (item: ISpeedDialRecord) => {
      editSpeedDial(dispatch, item);
      setCurrentView(SpeedDialView.EDITVIEW);
      if (onEditPress) {
        onEditPress(item);
      }
    };

    const handlePress = (item: ISpeedDialRecord) => {
      selectSpeedDial(dispatch, item);
      if (onPress) {
        if (item?.callType == "video") {
          submitMetrics({
            metricEvent: crossLaunchCallMetric,
            callMadeSection: `${speedDialLabel}`,
            callType: `${videoCall}`,
          });
        } else {
          submitMetrics({
            metricEvent: crossLaunchCallMetric,
            callMadeSection: `${speedDialLabel}`,
            callType: `${audioCall}`,
          });
        }
        onPress(item);
      }
    };

    const handleCancel = () => {
      setCurrentView(SpeedDialView.LIST);
      clearSpeedDial(dispatch);
    };

    const handleSubmit = (data: IFormData) => {
      const index = speedDials.findIndex(
        (e: ISpeedDialRecord) => e.id === data.id
      );
      if (index > -1) {
        updateSpeedDial(dispatch, data);
      } else {
        addSpeedDial(dispatch, data);
      }

      setCurrentView(SpeedDialView.LIST);
    };

    const handleSortEnd = (data: ISpeedDialRecord[]) => {
      if (onSortEnd) {
        onSortEnd(data);
      }
      reorderSpeedDial(dispatch, data);
    };

    const handleAddExisting = (item: ISpeedDialRecord) => {
      selectSpeedDial(dispatch, item);
      setCurrentView(SpeedDialView.SEARCHADDVIEW);
    };

    const handleAddNew = (data: any) => {
      setSearchText(data?.displayName);
      clearSpeedDial(dispatch);
      setCurrentView(SpeedDialView.ADDVIEW);
    };

    const handleRemovePress = (id: string) => {
      if (onRemovePress) {
        onRemovePress(id);
      }
      removeSpeedDial(dispatch, id);
    };

    const handleAudioCallPress = (item: ISpeedDialRecord) => {
      if (onAudioCallPress) {
        submitMetrics({
          metricEvent: crossLaunchCallMetric,
          callMadeSection: `${speedDialLabel}`,
          callType: `${audioCall}`,
        });
        onAudioCallPress(item);
      }
    };
    const handleVideoCallPress = (item: ISpeedDialRecord) => {
      if (onVideoCallPress) {
        submitMetrics({
          metricEvent: crossLaunchCallMetric,
          callMadeSection: `${speedDialLabel}`,
          callType: `${videoCall}`,
        });
        onVideoCallPress(item);
      }
    };

    const usedInPhoneTypes = (contactId: string) => {
      return speedDials
        .filter((item) => item.contactId === contactId)
        .map((item) => item.phoneType) as string[];
    };

    const handleEnterKeyPress = (
      event: React.KeyboardEvent<HTMLButtonElement>
    ) => {
      if (event.key === "Enter" || event.keyCode === 13) {
        setTimeout(() => {
          focusSpeedDialAddButton && focusSpeedDialAddButton();
        }, 200);
      }
    };

    useEffect(() => {
      const isDisabled = speedDials?.length >= 20 ? true : false;
      addSpeedDialDisabled(isDisabled);
    }, [speedDials]);

    useEffect(() => {
      const handleSpeedDialNotUptoDate = (event: StorageEvent) => {
        const isSpeedDialNotUptoDate = getLocalStorageItem<string>(
          SPEED_DIAL_NOT_UPTO_DATE
        );
        if (isSpeedDialNotUptoDate) {
          refresh();
          removeLocalStorageItem(SPEED_DIAL_NOT_UPTO_DATE);
        }
      };
      window.addEventListener("storage", handleSpeedDialNotUptoDate);
      return () => {
        window.removeEventListener("storage", handleSpeedDialNotUptoDate);
      };
    }, []);

    useEffect(() => {
      const handleLocalStorageChange = (event: StorageEvent) => {
        const stringValue = getLocalStorageItem<string>(SD_ERROR_LIST_MSG);
        if (stringValue) {
          setSpeedDialListError(true);
        } else {
          setSpeedDialListError(false);
        }
      };
      window.addEventListener("storage", handleLocalStorageChange);

      return () => {
        window.removeEventListener("storage", handleLocalStorageChange);
      };
    }, []);

    useEffect(() => {
      if (
        sdFailureAdd ||
        sdFailureDelete ||
        sdFailureEdit ||
        sdFailureReorder
      ) {
        setCurrentView(SpeedDialView.ERRORVIEW);
      }
      if (sdFailureAdd) {
        setErrorText(SD_ERROR_NOTIFICATION.SD_CREATE);
      } else if (sdFailureDelete) {
        setErrorText(SD_ERROR_NOTIFICATION.SD_DELETE);
      } else if (sdFailureEdit) {
        setErrorText(SD_ERROR_NOTIFICATION.SD_EDIT);
      } else if (sdFailureReorder) {
        setErrorText(SD_ERROR_NOTIFICATION.SD_REORDER);
      }
    }, [sdFailureAdd, sdFailureDelete, sdFailureEdit, sdFailureReorder]);

    useEffect(() => {
      if (currentView === SpeedDialView.SEARCHVIEW) {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }
    }, [currentView, setCurrentView]);

    useEffect(() => {
      const handleSpeedDialAddRefresh = (event: StorageEvent) => {
        const isSdAdd = getLocalStorageItem<string>(
          SD_SUCCESS_NOTICATION.SD_SUCCESS_ADD
        );
        if (isSdAdd) {
          refresh();
        }
      };
      window.addEventListener("storage", handleSpeedDialAddRefresh);

      return () => {
        window.removeEventListener("storage", handleSpeedDialAddRefresh);
      };
    }, []);

    return (
      <div className={cssClasses} style={style}>
        {/*  List View */}
        {loading && <LoadingSpinner scale={48} data-testid="loading-spinner" />}
        {speedDials && !loading && (
          <SpeedDials
            items={speedDialsWithPresence}
            onSortEnd={handleSortEnd}
            onPress={handlePress}
            onRemovePress={handleRemovePress}
            onEditPress={handleEditPress}
            onAudioCallPress={handleAudioCallPress}
            onVideoCallPress={handleVideoCallPress}
            onAddPress={onAddPress}
            listError={speedDialListError}
          />
        )}

        {/*  Search View */}
        {currentView === SpeedDialView.SEARCHVIEW && (
          <FocusLock group={CALLING_SPEED_DIAL_FOCUS_GROUP}>
            <SpeedDialModal
              onCancel={handleCancel}
              headerText={addHeaderText}
              handleEnterKeyPress={handleEnterKeyPress}
            >
              <WebexSpeedDialSearch
                items={contacts}
                onAdd={handleAddNew}
                onPress={handleAddExisting}
                searchInputRef={searchInputRef}
                headerText={addHeaderText}
                theme={theme}
              />
            </SpeedDialModal>
          </FocusLock>
        )}
        {/*  Add View */}
        {currentView === SpeedDialView.ADDVIEW && (
          <FocusLock group={CALLING_SPEED_DIAL_FOCUS_GROUP}>
            <div
              aria-modal="true"
              role="dialog"
              aria-label={t("webex.calling") + ", " + addHeaderText}
            >
              <SpeedDialModal
                onCancel={handleCancel}
                headerText={addHeaderText}
                handleEnterKeyPress={handleEnterKeyPress}
              >
                <SpeedDialForm
                  addText={t("modal.add.label")}
                  cancelText={t("modal.cancel.label")}
                  data={selectedItem}
                  onCancel={handleCancel}
                  onSubmit={handleSubmit}
                  searchText={searchText}
                  handleEnterKeyPress={handleEnterKeyPress}
                  headerText={addHeaderText}
                />
              </SpeedDialModal>
            </div>
          </FocusLock>
        )}
        {/*  SearchAddView View */}
        {currentView === SpeedDialView.SEARCHADDVIEW && (
          <FocusLock group={CALLING_SPEED_DIAL_FOCUS_GROUP}>
            <div
              aria-modal="true"
              role="dialog"
              aria-label={t("webex.calling") + ", " + addHeaderText}
            >
              <SpeedDialModal
                onCancel={handleCancel}
                headerText={addHeaderText}
                handleEnterKeyPress={handleEnterKeyPress}
              >
                <SpeedDialForm
                  addText={t("modal.add.label")}
                  cancelText={t("modal.cancel.label")}
                  onSubmit={handleSubmit}
                  onCancel={handleCancel}
                  data={selectedItem}
                  isContact={selectedItem?.currentCallAddress !== null}
                  usedPhonesList={usedInPhoneTypes(
                    selectedItem?.contactId as string
                  )}
                  inUseText={t("inUse.label")}
                  inUsePlaceHolder={t("inUse.placeholder")}
                  handleEnterKeyPress={handleEnterKeyPress}
                  headerText={addHeaderText}
                />
              </SpeedDialModal>
            </div>
          </FocusLock>
        )}
        {/*  Edit View */}
        {currentView === SpeedDialView.EDITVIEW && (
          <FocusLock group={CALLING_SPEED_DIAL_FOCUS_GROUP}>
            <div
              aria-modal="true"
              role="dialog"
              aria-label={t("webex.calling") + ", " + editHeaderText}
            >
              <SpeedDialModal
                onCancel={handleCancel}
                headerText={editHeaderText}
                isEdit={selectedItem?.currentCallAddress !== null}
              >
                <SpeedDialForm
                  addText={t("modal.edit.label")}
                  cancelText={t("modal.cancel.label")}
                  isEdit={selectedItem?.currentCallAddress !== null}
                  onSubmit={handleSubmit}
                  onCancel={handleCancel}
                  data={speedDials[selectedIndex]}
                  headerText={editHeaderText}
                />
              </SpeedDialModal>
            </div>
          </FocusLock>
        )}
        {/*  Error View */}
        {currentView === SpeedDialView.ERRORVIEW && (
          <FocusLock group={CALLING_SPEED_DIAL_FOCUS_GROUP}>
            <div aria-modal="true" role="alert">
              <SpeedDialErrorModal
                onCancel={handleCancel}
                errorNotification={errorText}
                removeSdFailureStorageEvents={removeSdFailureStorageEvents}
                refresh={refresh}
              ></SpeedDialErrorModal>
            </div>
          </FocusLock>
        )}
      </div>
    );
  }
);

WebexSpeedDials.defaultProps = {
  contacts: undefined,
  onSortEnd: undefined,
};
